import { useEffect } from 'react';
import { observer } from 'mobx-react';
import {
  Input,
  Select,
  Button,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  ClearOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import type { ScenarioCategoryDto } from '@/api/scenarios';
import { colors } from '@/theme/tokens';
import { AgentPickerTrigger } from '@/components/AgentPickerDrawer';
import { TagsInput } from '@/components/TagsInput';
import { HelpHotspot } from '@/features/help/HelpHotspot';

// ─────────────────────────────────────────────────────────────────────────────
// ScenarioFilterBar — single-row, slice-many-ways filter for the library
//
// REPLACES
//   The old left-rail agent picker + safety-toggle + category chip strip.
//   Everything is now in one Linear-style filter row so engineers and SMEs
//   can compose filters without scanning multiple regions of the page.
//
// VERTICAL-AGNOSTIC
//   Category labels come from the `aivalidation.ScenarioCategory` API
//   (see `useStore().scenariosStore.categoryCatalog`), not from a hard-coded
//   switch. Audience is a free-string dropdown sourced from the agents
//   in the current result set.
//
// EVERYTHING IS FETCHED FROM THE SERVER
//   Filter setters mutate `params` in the store and re-fetch with a small
//   debounce. The free-text Search box is the only client-side filter —
//   it stays instant and applies on top of the server-filtered set.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  /** Pin the filter bar to a specific domain (e.g. "oncology") to scope the category dropdown. */
  domain?: string;
}

export const ScenarioFilterBar = observer(function ScenarioFilterBar({ domain }: Props) {
  const { scenariosStore: store } = useStore();

  // Pull the per-domain category catalog once.
  useEffect(() => {
    if (store.categoryCatalog.length === 0) {
      void store.fetchCategoryCatalog(domain);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain]);

  // Local mirror of the comma-separated `categories`/`tags` strings so
  // the multi-select UIs work with arrays. Kept in sync with the store
  // params on every render — single source of truth is still the store.
  const selectedCategories = store.params.categories
    ? store.params.categories.split(',').filter(Boolean)
    : [];
  const selectedTags = store.params.tags
    ? store.params.tags.split(',').filter(Boolean)
    : [];

  // Categories visible in the dropdown — scoped by domain when the filter
  // bar was given one. Falls back to "all active" when no domain is pinned.
  const categoryOptions = categoryOptionsByCode(
    store.categoryCatalog.filter((c) => !domain || c.domain === domain)
  );

  return (
    <div
      style={{
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
      }}
    >
      <FilterOutlined style={{ color: colors.slate[400], fontSize: 14 }} />
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: colors.slate[500],
        }}
      >
        Filters
      </span>
      {/* Layman-language explainer — opens the help drawer with "what each
          filter does" written for non-engineers. The user explicitly asked
          for help on every screen in the validation area. */}
      <HelpHotspot topic="validations.scenarios.filter" variant="inline" />

      {/* Free-text search — stays client-side. Instant feel; applies on the
          already-fetched (server-filtered) result set. */}
      <Input
        allowClear
        prefix={<SearchOutlined style={{ color: colors.slate[400] }} />}
        placeholder="Search by id, description, tags…"
        value={store.search}
        onChange={(e) => store.setSearch(e.target.value)}
        style={{ flex: 1, minWidth: 240 }}
      />

      {/* Agent — primary slice. Uses the canonical AgentPickerTrigger so
          the experience matches Activity / Recent Runs. */}
      <AgentPickerTrigger
        value={store.params.agentId ?? null}
        onChange={(id) => store.setAgentFilter(id)}
        placeholder="Any agent"
        size="middle"
      />

      {/* Audience — free-string sourced from the current result set. We
          rebuild this list as the result set changes; on a wide search
          you get every audience the merchant uses. */}
      <Select
        allowClear
        placeholder="Any audience"
        style={{ minWidth: 160 }}
        value={store.params.audience ?? undefined}
        onChange={(v) => store.setAudienceFilter(v ?? null)}
        options={store.audiences.map((a) => ({ value: a, label: a }))}
      />

      {/* Single vs multi-turn — derived from TurnCount column. */}
      <Select
        allowClear
        placeholder="Any turns"
        style={{ minWidth: 130 }}
        value={store.params.turnCount ?? undefined}
        onChange={(v) => store.setTurnCountFilter((v as 'multi' | 'single' | undefined) ?? null)}
        options={[
          { value: 'single', label: 'Single-turn' },
          { value: 'multi', label: 'Multi-turn' },
        ]}
      />

      {/* Category — multi-select, sourced from the per-domain taxonomy table. */}
      <Select
        mode="multiple"
        allowClear
        placeholder="Any category"
        style={{ minWidth: 200, maxWidth: 320 }}
        value={selectedCategories}
        onChange={(v) => store.setCategoriesFilter(v as string[])}
        options={categoryOptions}
        optionFilterProp="label"
        popupMatchSelectWidth={380}
        optionRender={(opt) => {
          const elsewhere = (opt.data as CategoryOption).elsewhere;
          return (
            <div style={{ padding: '2px 0' }}>
              <div style={{ whiteSpace: 'normal' }}>{opt.label}</div>
              {elsewhere && (
                <div style={{ fontSize: 11.5, color: colors.slate[500], whiteSpace: 'normal' }}>{elsewhere}</div>
              )}
            </div>
          );
        }}
        maxTagCount="responsive"
      />

      {/* Has-banned-phrases — pill-style toggle. We bias toward "show only
          true" because that's the leading reason an engineer reaches for
          this filter (chasing wording regressions). Three states: off /
          show only true / show only false. */}
      <Tooltip title="Scenarios that explicitly forbid certain wording in the agent reply">
        <Select
          allowClear
          placeholder="Banned phrases?"
          style={{ minWidth: 170 }}
          value={
            store.params.hasBannedPhrases == null
              ? undefined
              : store.params.hasBannedPhrases ? 'yes' : 'no'
          }
          onChange={(v) =>
            store.setHasBannedPhrases(v === 'yes' ? true : v === 'no' ? false : null)
          }
          options={[
            { value: 'yes', label: 'Has banned phrases' },
            { value: 'no', label: 'No banned phrases' },
          ]}
        />
      </Tooltip>

      {/* Has-skill-assertions — pill-style toggle. Same UX pattern as banned
          phrases. Useful for "after I changed the skill router, which
          scenarios should I run first?" */}
      <Tooltip title="Scenarios that pin which skills the agent must / must not load">
        <Select
          allowClear
          placeholder="Skill assertions?"
          style={{ minWidth: 170 }}
          value={
            store.params.hasSkillAssertions == null
              ? undefined
              : store.params.hasSkillAssertions ? 'yes' : 'no'
          }
          onChange={(v) =>
            store.setHasSkillAssertions(v === 'yes' ? true : v === 'no' ? false : null)
          }
          options={[
            { value: 'yes', label: 'Asserts skill routing' },
            { value: 'no', label: 'No skill assertions' },
          ]}
        />
      </Tooltip>

      {/* Tags — free-form multi-input. Type a substring and press Enter;
          server-side does an OR of LIKE %tag% matches. */}
      <TagsInput
        value={selectedTags}
        onChange={(tags) => store.setTagsFilter(tags)}
      />

      {store.activeFilterCount > 0 && (
        <Button
          type="link"
          size="small"
          icon={<ClearOutlined />}
          onClick={() => store.clearFilters()}
        >
          Clear filters
        </Button>
      )}
    </div>
  );
});

// ── Category options ───────────────────────────────────────────────────────
//
// One option per code. Each area keeps its own copy of the taxonomy (eight areas
// on 2026-11-01), so one row per category listed every code eight times. A code
// does not always mean the same thing — "A" is "Safety / critical" in most areas
// and "Orientation / scope" in prior-auth — and the filter matches the code in
// every area, so the option names the meaning most areas use and says where it
// means something else.

interface CategoryOption {
  value: string;
  label: string;
  /** e.g. "In prior-auth: Orientation / scope"; empty when every area agrees. */
  elsewhere: string;
}

function categoryOptionsByCode(rows: ScenarioCategoryDto[]): CategoryOption[] {
  const areasByMeaning = new Map<string, Map<string, string[]>>();
  for (const row of rows) {
    const meanings = areasByMeaning.get(row.code) ?? new Map<string, string[]>();
    const meaning = row.displayName || row.code;
    meanings.set(meaning, [...(meanings.get(meaning) ?? []), row.domain]);
    areasByMeaning.set(row.code, meanings);
  }
  return [...areasByMeaning.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([code, meanings]) => {
      const [main, ...others] = [...meanings.entries()].sort(
        ([nameA, areasA], [nameB, areasB]) => areasB.length - areasA.length || nameA.localeCompare(nameB)
      );
      return {
        value: code,
        label: main[0] === code ? code : `${code} · ${main[0]}`,
        elsewhere: others.map(([name, areas]) => `In ${areas.join(', ')}: ${name}`).join(' · '),
      };
    });
}
